import { cn } from "@gymflow/helpers";
import { UserMemberSignupAsStaffDTO } from "@gymflow/types";
import { FormikProvider, useFormik } from "formik";
import { useMutationCreateUser } from "libs/api/src/lib/hooks/member/useMutationCreateUser";
import { useMutationEmailExists } from "libs/api/src/lib/hooks/memberAsPublic/useMutationEmailExists";
import { SignUpSchemaAsStaff } from "libs/validation/src/SignUpSchema";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import useGymflowModels from "../../store";
import { Button } from "../atoms";
import { FormInput } from "../atoms/form/FormInput";
import { FormPhoneNumberInput } from "../atoms/form/FormPhoneNumberInput";

export type SideBarUserFormProps = {
  className?: string;
  onMemberCreated: (newUserId: string) => void;
  defaultValues: UserMemberSignupAsStaffDTO;
};

export const SideBarUserFormNew = ({
  className,
  onMemberCreated,
  defaultValues,
}: SideBarUserFormProps) => {
  const { api } = useGymflowModels();
  const { t } = useTranslation();
  const { mutateAsync: emailExists } = useMutationEmailExists({
    api,
  });
  const { mutateAsync: createUser } = useMutationCreateUser({
    api,
  });
  const PortalSignUpSchema = useMemo(
    () => SignUpSchemaAsStaff(async (email) => await emailExists({ email })),
    [emailExists],
  );
  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: toFormikValidationSchema(PortalSignUpSchema),
    validate: toFormikValidate(PortalSignUpSchema),
    onSubmit: async (_values) => {
      const values = await PortalSignUpSchema.parseAsync(_values);

      const { userMemberId } = await createUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNumber: values.mobileNumber,
      });
      onMemberCreated(userMemberId);
    },
  });
  return (
    <FormikProvider value={formik}>
      <div
        className={cn(
          "flex size-full flex-col  min-h-fit gap-4 justify-between",
          className,
        )}
      >
        <div className="flex flex-col gap-4">
          <FormInput
            name="email"
            label={t("page.hostedPages.signUp.email")}
            placeholder={t("page.hostedPages.signUp.email")}
            containerClassName="w-full"
          />

          <FormInput
            name="firstName"
            label={t("page.hostedPages.signUp.firstName")}
            placeholder={t("page.hostedPages.signUp.firstName")}
            containerClassName="w-full"
          />
          <FormInput
            name="lastName"
            label={t("page.hostedPages.signUp.lastName")}
            placeholder={t("page.hostedPages.signUp.lastName")}
            containerClassName="w-full"
          />
          <FormPhoneNumberInput
            name="mobileNumber"
            label={t("page.hostedPages.signUp.mobileNumber")}
            placeholder={t("page.hostedPages.signUp.mobileNumber")}
            className="w-full"
          />
        </div>

        <Button
          className="w-full"
          intent="secondary"
          onClick={async () => {
            await formik.submitForm();
          }}
        >
          {t("page.newUserSideBar.createUser")}
        </Button>
      </div>
    </FormikProvider>
  );
};
